/**
 * Beast Games Promotional Popup
 * Zeigt einmal pro Session auf der Homepage
 * Basiert auf Cookie-Banner Pattern für Modal-Logik
 */
class BeastPromoPopup {
  constructor() {
    this.STORAGE_KEY = 'beast-promo-shown';

    this.popup = document.querySelector('[data-promo-popup]');
    this.overlay = document.querySelector('[data-promo-overlay]');
    this.closeBtn = document.querySelector('[data-promo-close]');
    this.ctaBtn = document.querySelector('[data-promo-cta]');

    // Focus Trap für Accessibility
    this.lastFocusedElement = null;
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;

    if (this.popup) {
      this.init();
    }
  }

  init() {
    // Nur zeigen wenn noch nicht in dieser Session gezeigt
    if (this.shouldShow()) {
      // Delay für bessere UX (Seite erst laden lassen)
      setTimeout(() => this.show(), 500);
    }

    this.bindEvents();
  }

  bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', () => this.close());

    // CTA button (schließt popup UND navigiert)
    this.ctaBtn?.addEventListener('click', () => this.close());

    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());

    // Keyboard handlers
    document.addEventListener('keydown', (e) => this.handleKeydown(e));
  }

  shouldShow() {
    // Check 1: Bereits in dieser Session gezeigt?
    if (sessionStorage.getItem(this.STORAGE_KEY)) return false;

    // Check 2: Startdatum noch nicht erreicht?
    if (this.isNotYetStarted()) return false;

    // Check 3: Enddatum überschritten?
    if (this.isExpired()) return false;

    return true;
  }

  isNotYetStarted() {
    const startDateStr = this.popup?.dataset.startDate; // z.B. "2027-01-01"
    if (!startDateStr) return false; // Kein Startdatum = sofort aktiv

    try {
      // Parse ISO date string (YYYY-MM-DD), aktiv ab Beginn des Tages (00:00:00 local time)
      const startDate = new Date(startDateStr + 'T00:00:00');
      const now = new Date();

      return now < startDate;
    } catch (error) {
      // Graceful degradation: Bei ungültigem Datum sofort aktiv
      console.warn('Beast Promo Popup: Invalid start date format:', startDateStr);
      return false;
    }
  }

  isExpired() {
    const endDateStr = this.popup?.dataset.endDate; // z.B. "2027-01-06"
    if (!endDateStr) return false; // Kein Enddatum = nie expired

    try {
      // Parse ISO date string (YYYY-MM-DD) und setze auf Ende des Tages (23:59:59 local time)
      const endDate = new Date(endDateStr + 'T23:59:59');
      const now = new Date();

      return now > endDate;
    } catch (error) {
      // Graceful degradation: Bei ungültigem Datum nicht als expired markieren
      console.warn('Beast Promo Popup: Invalid end date format:', endDateStr);
      return false;
    }
  }

  show() {
    // Mark als gezeigt für diese Session
    sessionStorage.setItem(this.STORAGE_KEY, 'true');

    // Show popup und overlay
    this.popup?.classList.add('active');
    this.overlay?.classList.add('active');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Store last focused element
    this.lastFocusedElement = document.activeElement;

    // Setup focus trap
    this.setupFocusTrap();

    // Focus close button nach Animation
    setTimeout(() => this.closeBtn?.focus(), 350);
  }

  close() {
    // Hide popup und overlay
    this.popup?.classList.remove('active');
    this.overlay?.classList.remove('active');

    // Restore body scroll
    document.body.style.overflow = '';

    // Return focus
    if (this.lastFocusedElement) {
      this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }
  }

  setupFocusTrap() {
    if (this.popup) {
      this.focusableElements = Array.from(
        this.popup.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );
      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }
  }

  handleKeydown(e) {
    if (!this.popup?.classList.contains('active')) return;

    // Close on Escape
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    // Focus Trap on Tab
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === this.firstFocusable) {
          e.preventDefault();
          this.lastFocusable?.focus();
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === this.lastFocusable) {
          e.preventDefault();
          this.firstFocusable?.focus();
        }
      }
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  window.beastPromoPopup = new BeastPromoPopup();
});
